import React, { useEffect, useMemo, useState } from 'react';
import analyticsService, { DeliverabilityResults } from '../../../services/analyticsService';
import emailService, { EmailMailbox } from '../../../services/emailService';
import deliverabilityService, { DeliverabilityAlert } from '../../../services/deliverabilityService';

const EmailDeliverabilityPanel: React.FC = () => {
  const [mailboxes, setMailboxes] = useState<EmailMailbox[]>([]);
  const [selectedMailboxId, setSelectedMailboxId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [seedRunning, setSeedRunning] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [premiumBlocked, setPremiumBlocked] = useState<boolean>(false);
  const [results, setResults] = useState<DeliverabilityResults | null>(null);
  const [queuedSeedRun, setQueuedSeedRun] = useState<boolean>(false);
  const [alerts, setAlerts] = useState<DeliverabilityAlert[] | null>(null);
  const [alertsLoading, setAlertsLoading] = useState<boolean>(false);

  // Load mailboxes
  useEffect(() => {
    const loadMailboxes = async () => {
      try {
        const m = await emailService.getMailboxes();
        setMailboxes(m);
        if (m.length > 0 && !selectedMailboxId) setSelectedMailboxId(m[0].id as string);
      } catch (e) {
        console.error('Failed to load mailboxes', e);
        setError('Failed to load mailboxes');
      }
    };
    loadMailboxes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedMailbox = useMemo(() => mailboxes.find(m => (m.id as string) === selectedMailboxId) || null, [mailboxes, selectedMailboxId]);

  // Fetch deliverability results on mailbox change
  useEffect(() => {
    const loadResults = async () => {
      if (!selectedMailboxId) return;
      try {
        setError(null);
        setPremiumBlocked(false);
        setLoading(true);
        const r = await analyticsService.getDeliverabilityResults(selectedMailboxId, 30);
        setResults(r);
      } catch (e: any) {
        console.error('Failed to load deliverability results', e);
        const msg = (e?.message || '').toLowerCase();
        if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
          setPremiumBlocked(true);
        } else {
          setError('Failed to load deliverability results');
        }
        setResults(null);
      } finally {
        setLoading(false);
      }
    };
    loadResults();
  }, [selectedMailboxId]);

  // Fetch deliverability alerts on mailbox change
  useEffect(() => {
    const loadAlerts = async () => {
      if (!selectedMailboxId) return;
      try {
        setPremiumBlocked(false);
        setAlertsLoading(true);
        const resp = await deliverabilityService.getAlerts({ mailboxId: selectedMailboxId, sinceDays: 14 });
        setAlerts(resp.alerts || []);
      } catch (e: any) {
        console.error('Failed to load deliverability alerts', e);
        const msg = (e?.message || '').toLowerCase();
        if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
          setPremiumBlocked(true);
        } else {
          // Do not set global error for alerts; keep panel usable
          setAlerts(null);
        }
      } finally {
        setAlertsLoading(false);
      }
    };
    loadAlerts();
  }, [selectedMailboxId]);

  // If user clicks Run Seed before mailboxes are loaded, queue it and run when mailboxes arrive
  useEffect(() => {
    if (queuedSeedRun && mailboxes.length > 0 && !seedRunning) {
      setQueuedSeedRun(false);
      // Trigger with the first mailbox id
      const firstId = mailboxes[0]?.id as string | undefined;
      if (firstId) {
        // align state for consistency
        if (!selectedMailboxId) setSelectedMailboxId(firstId);
        // run
        onRunSeedCheck();
      }
    }
  }, [queuedSeedRun, mailboxes, seedRunning, selectedMailboxId]);

  const onRunSeedCheck = async () => {
    // Fallback to first mailbox id if selection not yet set
    const mailboxId = selectedMailboxId || (mailboxes[0]?.id as string | undefined);
    if (!mailboxId) {
      // queue a run for when mailboxes load
      setQueuedSeedRun(true);
      return;
    }
    try {
      setSeedRunning(true);
      setError(null);
      setPremiumBlocked(false);
      // If we just derived mailboxId, sync state for consistency
      if (!selectedMailboxId && mailboxId) setSelectedMailboxId(mailboxId);
      await analyticsService.runSeedCheck(mailboxId);
      // Immediately refetch results (MVP)
      const r = await analyticsService.getDeliverabilityResults(mailboxId, 30);
      setResults(r);
    } catch (e: any) {
      console.error('Seed check failed', e);
      const msg = (e?.message || '').toLowerCase();
      if (msg.includes('premium') || msg.includes('forbidden') || msg.includes('403')) {
        setPremiumBlocked(true);
      } else {
        setError('Failed to start seed check');
      }
    } finally {
      setSeedRunning(false);
    }
  };

  return (
    <div className="bg-white p-4 rounded-lg shadow border border-gray-200">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">Deliverability Monitoring</h3>
        <div className="flex items-center space-x-2">
          <select
            className="border border-gray-300 rounded px-3 py-2 text-sm"
            value={selectedMailboxId}
            onChange={(e) => setSelectedMailboxId(e.target.value)}
            aria-label="Select deliverability mailbox"
            aria-hidden="true"
          >
            {mailboxes.length === 0 && <option value="">No mailboxes</option>}
            {mailboxes.map((m) => (
              <option key={m.id as string} value={m.id as string}>
                {m.name} ({m.email})
              </option>
            ))}
          </select>
          <button
            onClick={onRunSeedCheck}
            disabled={seedRunning}
            className={`px-4 py-2 rounded text-white ${seedRunning ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
          >
            {seedRunning ? 'Running...' : 'Run Seed Check'}
          </button>
        </div>
      </div>

      {premiumBlocked && (
        <div className="p-4 mb-4 bg-yellow-50 border border-yellow-200 rounded">
          <p className="text-yellow-700 text-sm">Premium feature. Please upgrade to access deliverability monitoring.</p>
        </div>
      )}

      {error && (
        <div className="p-4 mb-4 bg-red-50 border border-red-200 rounded">
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {loading && (
        <div className="h-32 flex items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
        </div>
      )}

      {!loading && results && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 rounded border border-gray-200">
            <p className="text-sm text-gray-500">Domain</p>
            <p className="text-lg font-semibold">{results.domain}</p>
            <p className="text-xs text-gray-400 mt-1">Since {new Date(results.since).toLocaleDateString()}</p>
          </div>
          <div className="p-4 rounded border border-gray-200">
            <p className="text-sm text-gray-500">Open Rate</p>
            <p className="text-2xl font-bold text-blue-600">{results.rates.open_rate.toFixed(1)}%</p>
            <p className="text-xs text-gray-400 mt-1">Sent: {results.totals.totalSent}</p>
          </div>
          <div className="p-4 rounded border border-gray-200">
            <p className="text-sm text-gray-500">Reply Rate</p>
            <p className="text-2xl font-bold text-purple-600">{results.rates.reply_rate.toFixed(1)}%</p>
            <p className="text-xs text-gray-400 mt-1">Bounces: {results.totals.totalBounced}</p>
          </div>

          <div className="md:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-500">Sender Score</p>
              <p className="text-2xl font-bold text-gray-800">{results.checks.sender_score}</p>
            </div>
            <div className="p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-500">DKIM</p>
              <span className={`px-2 py-1 text-xs font-semibold rounded ${results.checks.dkim_pass === true ? 'bg-green-100 text-green-800' : results.checks.dkim_pass === false ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                {results.checks.dkim_pass === null ? 'Unknown' : results.checks.dkim_pass ? 'Pass' : 'Fail'}
              </span>
            </div>
            <div className="p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-500">SPF</p>
              <span className={`px-2 py-1 text-xs font-semibold rounded ${results.checks.spf_pass === true ? 'bg-green-100 text-green-800' : results.checks.spf_pass === false ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                {results.checks.spf_pass === null ? 'Unknown' : results.checks.spf_pass ? 'Pass' : 'Fail'}
              </span>
            </div>
            <div className="p-4 rounded border border-gray-200">
              <p className="text-sm text-gray-500">DMARC</p>
              <span className={`px-2 py-1 text-xs font-semibold rounded ${results.checks.dmarc_pass === true ? 'bg-green-100 text-green-800' : results.checks.dmarc_pass === false ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                {results.checks.dmarc_pass === null ? 'Unknown' : results.checks.dmarc_pass ? 'Pass' : 'Fail'}
              </span>
            </div>
          </div>
        </div>
      )}

      {!loading && !results && !premiumBlocked && (
        <div className="p-4 rounded border border-gray-200 text-gray-600">No results yet. Run a seed check to generate deliverability insights.</div>
      )}

      {/* Alerts Section */}
      {!premiumBlocked && (
        <div className="mt-4">
          <h4 className="text-md font-semibold text-gray-800 mb-2">Deliverability Alerts</h4>
          {alertsLoading && (
            <div className="h-10 flex items-center text-sm text-gray-500">Loading alerts…</div>
          )}
          {!alertsLoading && alerts && alerts.length === 0 && (
            <div className="p-3 rounded border border-gray-200 text-sm text-gray-600">No alerts in the last 14 days.</div>
          )}
          {!alertsLoading && alerts && alerts.length > 0 && (
            <ul className="space-y-2">
              {alerts.map((a, idx) => (
                <li key={idx} className="p-3 rounded border border-gray-200 flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-800">{a.message}</p>
                    <p className="text-xs text-gray-500 mt-1">Code: {a.code}</p>
                  </div>
                  <span
                    className={
                      'ml-3 inline-flex items-center px-2 py-1 rounded text-xs font-semibold ' +
                      (a.level === 'crit'
                        ? 'bg-red-100 text-red-800'
                        : a.level === 'warn'
                        ? 'bg-yellow-100 text-yellow-800'
                        : 'bg-blue-100 text-blue-800')
                    }
                    aria-label={`severity-${a.level}`}
                  >
                    {a.level.toUpperCase()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {selectedMailbox && (
        <p className="mt-3 text-xs text-gray-400">Mailbox: {selectedMailbox.name} ({selectedMailbox.email})</p>
      )}
    </div>
  );
};

export default EmailDeliverabilityPanel;
